<template>
  <HgModal
    v-model="isModalVisible"
    :closeButtonCssClass="$style.modalClose"
    :containerCssClass="$style.modalContainer"
    @close="handleCancelClick"
  >
    <SearchFilters
      :propertyType="scopedPropertyType"
      :searchLocation="scopedSearchLocation"
      dropdownLayerSize="small"
      @onPropertyTypeChange="handlePropertyTypeChange"
      @onSelectedLocationChange="handleSelectedLocationChange"
    />
    <template #footer>
      <HgButton theme="primary-bordered" @click="handleCancelClick">{{
        t('common.cancel')
      }}</HgButton>
      <HgButton :disabled="isLoading || searchButtonDisabled" @click="handleSearchClick">{{
        t('common.search')
      }}</HgButton>
    </template>
  </HgModal>
</template>
<script setup lang="ts">
import { HgButton, HgModal, HgSupportedLanguage } from '@smg-real-estate/domus';
import { type GeoLocation } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import SearchFilters from '@/components/search-bar/SearchFilters.vue';
import useRouterLocationPush from '@/composables/useRouterLocationPush';
import { getCulture } from '@/helpers/localizeLanguage';
import { PropertyType } from '@/models/iazi/common/enums';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import { trackLocationSearch } from '@/tracking';
import { EventSource } from '@/types';

interface Props {
  visible: boolean;
}

const props = defineProps<Props>();

const { t, locale } = useI18n();

const isModalVisible = ref(props.visible);
const mapStore = useMapStore();
const { isLoading, propertyType } = storeToRefs(mapStore);
const geoStore = useGeoStore();
const { searchLocation, getIaziLevelByGeoType } = storeToRefs(geoStore);
const { pushLocation } = useRouterLocationPush();

const scopedPropertyType = ref<PropertyType>(propertyType.value);
const scopedSearchLocation = ref<GeoLocation | null>(searchLocation.value);

watch(
  () => props.visible,
  (value) => {
    isModalVisible.value = value;
  },
);

const emit = defineEmits<{
  // TODO find the lint rule to match the Composition API
  /* eslint-disable unused-imports/no-unused-vars */
  (e: 'onCancelClick' | 'onSearchComplete'): void;
}>();

watch(searchLocation, () => {
  scopedSearchLocation.value = searchLocation.value;
});

const searchButtonDisabled = computed<boolean>(
  () =>
    propertyType.value === scopedPropertyType.value &&
    searchLocation.value?.id === scopedSearchLocation.value?.id,
);
const handlePropertyTypeChange = (value: PropertyType): void => {
  scopedPropertyType.value = value;
};
const handleSelectedLocationChange = (value: GeoLocation | null): void => {
  scopedSearchLocation.value = value;
};

const handleCancelClick = (): void => {
  emit('onCancelClick');
};
const handleSearchClick = async (): Promise<void> => {
  if (scopedPropertyType.value !== propertyType.value) {
    mapStore.setPropertyType(scopedPropertyType.value);
    mapStore.fetchPriceDistributions(
      scopedPropertyType.value,
      getCulture(locale.value as HgSupportedLanguage),
    );
  }
  if (searchLocation.value?.id !== scopedSearchLocation.value?.id) {
    mapStore.setLoading(true);
    await geoStore.setSearchLocation(scopedSearchLocation.value);
    mapStore.setCurrentLevel(getIaziLevelByGeoType.value);
    await pushLocation(EventSource.Search);
  }

  if (scopedSearchLocation.value?.id) {
    trackLocationSearch(scopedSearchLocation.value.id, propertyType.value);
  }

  emit('onSearchComplete');
};
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

.modalClose {
  display: none;
}

.modalContainer {
  align-self: flex-start;
  width: calc(100% - 2 * $space-100);
  margin-top: $space-150;

  @include grid-media-query(sm) {
    width: auto;
  }

  &[class*='Modal_dialog'] {
    > [class*='Modal_body'] {
      &[class*='Modal_noTitle'] {
        padding: $space-200 $space-100 $space-250 $space-100;
      }
    }
  }

  [class*='Modal_footer'] {
    > [class*='Modal_default'] {
      justify-content: space-between;
      width: 100%;
    }
  }
}
</style>
